// Keyboard Shortcuts Component
// =============================
// Provides keyboard shortcuts overlay and global shortcut handling

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface Shortcut {
  key: string;
  description: string;
  path?: string;
  global?: boolean;
}

interface KeyboardShortcutsProps {
  isOpen: boolean;
  onClose: () => void;
}

// Define shortcuts configuration (Feature #24 - /topics renamed to /library)
const shortcutConfig: Shortcut[] = [
  { key: '?', description: 'Vis/skjul genveje', global: true },
  { key: 'g d', description: 'Gå til Dashboard', global: true, path: '/dashboard' },
  { key: 'g b', description: 'Gå til Mine læringsforløb', global: true, path: '/sessions' },
  { key: 'g s', description: 'Gå til Indstillinger', global: true, path: '/settings' },
  { key: 'n i', description: 'Ny introduktion-samtale', global: true, path: '/session/setup/introduction' },
  { key: 'n u', description: 'Ny undervisning-samtale', global: true, path: '/session/setup/education' },
  { key: 'n g', description: 'Ny gentagelse-samtale', global: true, path: '/session/setup/repetition' },
  { key: 'n e', description: 'Ny eksamenstræning-samtale', global: true, path: '/session/setup/training' },
  { key: 'Escape', description: 'Luk dialog/modal', global: true },
  { key: '/', description: 'Fokuser på søgefelt (hvis tilgængeligt)', global: false },
];

// Global keyboard shortcuts hook
export const useKeyboardShortcuts = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showShortcuts, setShowShortcuts] = useState(false);

  // Use refs to avoid stale closures
  const navigateRef = useRef(navigate);
  const showShortcutsRef = useRef(showShortcuts);

  // Keep refs up to date
  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  useEffect(() => {
    showShortcutsRef.current = showShortcuts;
  }, [showShortcuts]);

  // Handle key sequences (e.g., "g d" for go to dashboard)
  const keySequenceRef = useRef<string[]>([]);
  const sequenceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Don't trigger shortcuts when typing in inputs
    const target = e.target as HTMLElement;
    if (
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.isContentEditable
    ) {
      // Allow Escape in inputs
      if (e.key !== 'Escape') {
        return;
      }
    }

    // Handle ? for showing shortcuts
    if (e.key === '?' && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
      setShowShortcuts(prev => !prev);
      return;
    }

    // Handle Escape to close shortcuts overlay
    if (e.key === 'Escape') {
      if (showShortcutsRef.current) {
        e.preventDefault();
        setShowShortcuts(false);
      }
      return;
    }

    // Build key sequence
    const key = e.key.toLowerCase();

    // Clear existing timer
    if (sequenceTimerRef.current) {
      clearTimeout(sequenceTimerRef.current);
    }

    // Add to sequence
    keySequenceRef.current = [...keySequenceRef.current, key];

    // Set timer to clear sequence after 1 second
    sequenceTimerRef.current = setTimeout(() => {
      keySequenceRef.current = [];
    }, 1000);

    // Check for matching shortcuts
    const sequenceString = keySequenceRef.current.join(' ');
    const matchingShortcut = shortcutConfig.find(s => s.key.toLowerCase() === sequenceString);

    if (matchingShortcut?.path) {
      e.preventDefault();
      navigateRef.current(matchingShortcut.path);
      keySequenceRef.current = [];
      if (sequenceTimerRef.current) clearTimeout(sequenceTimerRef.current);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (sequenceTimerRef.current) clearTimeout(sequenceTimerRef.current);
    };
  }, [handleKeyDown]);

  // Close shortcuts when navigating
  useEffect(() => {
    setShowShortcuts(false);
  }, [location.pathname]);

  return {
    showShortcuts,
    setShowShortcuts,
    shortcuts: shortcutConfig,
    currentSequence: keySequenceRef.current,
  };
};

// Keyboard shortcuts overlay component
const KeyboardShortcutsOverlay: React.FC<KeyboardShortcutsProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const shortcuts = [
    { category: 'Navigation', items: [
      { key: 'g d', description: 'Gå til Dashboard' },
      { key: 'g b', description: 'Gå til Mine læringsforløb' },
      { key: 'g s', description: 'Gå til Indstillinger' },
    ]},
    { category: 'Samtaler', items: [
      { key: 'n i', description: 'Ny introduktion-samtale' },
      { key: 'n u', description: 'Ny undervisning-samtale' },
      { key: 'n g', description: 'Ny gentagelse-samtale' },
      { key: 'n e', description: 'Ny eksamenstræning-samtale' },
    ]},
    { category: 'Generelt', items: [
      { key: '?', description: 'Vis/skjul denne hjælp' },
      { key: 'Escape', description: 'Luk dialog eller overlay' },
    ]},
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="shortcuts-title"
    >
      <div
        className="bg-white dark:bg-neutral-dark rounded-xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 id="shortcuts-title" className="text-lg font-semibold text-neutral-dark dark:text-white flex items-center gap-2">
            <span className="text-xl">⌨️</span>
            Tastaturgenveje
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
            aria-label="Luk"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-neutral-medium" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>

        {/* Shortcuts List */}
        <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
          {shortcuts.map((category, categoryIndex) => (
            <div key={categoryIndex} className={categoryIndex > 0 ? 'mt-6' : ''}>
              <h3 className="text-sm font-medium text-neutral-medium uppercase tracking-wider mb-3">
                {category.category}
              </h3>
              <div className="space-y-2">
                {category.items.map((shortcut, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50"
                  >
                    <span className="text-neutral-dark dark:text-white">
                      {shortcut.description}
                    </span>
                    <div className="flex items-center gap-1">
                      {shortcut.key.split(' ').map((k, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && <span className="text-neutral-medium text-xs mx-1">derefter</span>}
                          <kbd className="px-2 py-1 text-xs font-mono bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded shadow-sm text-neutral-dark dark:text-white">
                            {k === 'Escape' ? 'Esc' : k.toUpperCase()}
                          </kbd>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700">
          <p className="text-xs text-neutral-medium text-center">
            Tryk <kbd className="px-1.5 py-0.5 text-xs font-mono bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded">?</kbd> når som helst for at vise denne hjælp
          </p>
        </div>
      </div>
    </div>
  );
};

export default KeyboardShortcutsOverlay;
